import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../utils/Auth';
import QrRef from '../QrRef';
import ColorPicker from './ColorPicker';
import cloneDeep from 'lodash.clonedeep';
import QRCodeStyling from '@plasticprintersinc/pp-qr-code';
import { useWindowSize } from '../../../utils/hooks/useWindowSize';
import styles from './QrFrames.module.css';

const FRAME_MAPPING = [
    { value: 'none', name: 'No Frame' },
    { value: 'scanme-bottom', name: 'Scan Me (Bottom)' },
    { value: 'scanme-top', name: 'Scan Me (Top)' },
    { value: 'menu-bottom', name: 'Menu (Bottom)' },
    { value: 'menu-top', name: 'Menu (Top)' },
    { value: 'arrow-bottom', name: 'Arrow (Bottom)' },
    { value: 'arrow-top', name: 'Arrow (Top)' }
];
const SIZE_MAPPING = ['300','500','750','1000','1500','2000'];
const TYPE_MAPPING = ['png','jpg','svg','webp'];
const CORRECTION_MAPPING = [
    { value: 'L', name: 'Low (~7%)' },
    { value: 'M', name: 'Medium (~15%)' },
    { value: 'Q', name: 'Quartile (~25%)' },
    { value: 'H', name: 'High (~30$)' }
];

export default function QrFrames() {

    const { qr_options: authQrOptions } = useAuth();
    const { width } = useWindowSize();
    const size = useMemo(() => {
        if (width <= 500) {
            return 265;
        } else if (width <= 768) {
            return 300;
        } else if (width <= 992) {
            return 275;
        } else {
            return 350;
        }
    },[width]);
    
    const [ options, setOptions ] = useState(null);
    // download only
    const [ fileType, setFileType ] = useState('png');
    const [ colorDisplay, setColorDisplay ] = useState('');
    const [ loading, setLoading ] = useState(false);
    

    useEffect(() => {
        setOptions(() => cloneDeep(authQrOptions))
    },[authQrOptions]);

    const handleSize = (e) => {
        const size = e.target.value;

        setOptions(prev => ({
            ...prev,
            width: parseInt(size),
            height: parseInt(size)
        }))
    };

    const handleCorrection = (e) => {
        const correction = e.target.value;

        setOptions(prev => ({
            ...prev,
            qrOptions: {
                ...prev.qrOptions,
                errorCorrectionLevel: correction
            }
        }))
    };

    const handleFrame = (e) => {
        const frame = e.target.value;

        setOptions(prev => ({
            ...prev,
            frameOptions: {
                ...prev.frameOptions,
                type: frame
            }
        }))
    };

    const handleColorChange = (color, name) => {
        setOptions(prev => ({
            ...prev,
            frameOptions: {
                ...prev.frameOptions,
                [`${name}Color`]: color
            }
        }))
    };

    const handleReset = () => {
        setOptions(prev => ({
            ...prev,
            frameOptions: {
                ...prev.frameOptions,
                primaryColor: '#000000',
                secondaryColor: '#FFFFFF',
                accentColor: '#000000'
            }
        }))
    };

    const handleDownload = async () => {
        const code = new QRCodeStyling(options);

        if (!code) {
            console.error('QR code not available for download');
            return;
        }

        try {
            setLoading(true);

            await code.download({
                extension: fileType === 'jpg' ? 'jpeg' : fileType,
                name: 'QRCode'
            });
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <div className={styles.header}>Frame Options</div>
                <div className={styles.words}>
                    Transform your QR code from a simple graphic into a stunning visual asset. 
                    Our intuitive design tool allows you to easily add a stylish frame, select a custom color to perfectly match your brand, 
                    and instantly download your unique, eye-catching QR code—all in one place.
                    <div className={styles.noFrameMessage}><i className='far fa-circle-info' />Select a frame to enable color pickers</div>
                </div>
                <div className={styles.styleWrap}>
                    <div className={styles.selectGroup}>
                        <div className={styles.inputGroup}>
                            <label htmlFor='qr-size' className={styles.label}>QR Size</label>
                            <select className={styles.select} id='qr-size' defaultValue='500' onChange={handleSize}>
                                {SIZE_MAPPING.map((value,i) => (
                                    <option key={i} value={value}>{value}px</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor='file-type' className={styles.label}>File Type</label>
                            <select className={styles.select} id='file-type' value={fileType} onChange={e => setFileType(e.target.value)}>
                                {TYPE_MAPPING.map((value,i) => (
                                    <option key={i} value={value}>{value.toUpperCase()}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup}>
                            <label htmlFor='correction-level' className={styles.label}>Error Correction</label>
                            <select className={styles.select} id='correction-level' value={options?.qrOptions?.errorCorrectionLevel} onChange={handleCorrection}>
                                {CORRECTION_MAPPING.map((c,i) => (
                                    <option key={i} value={c.value}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.inputGroup} style={{flexDirection: 'column', marginBottom: '.75rem'}}>
                            <label htmlFor='frame-design' className={styles.label}>Frame Design</label>
                            <select className={styles.select} id='frame-design' defaultValue='NoFrame' onChange={handleFrame}>
                                {FRAME_MAPPING.map((f,i) => (
                                    <option key={i} value={f.value}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className={`${styles.colorWrap} ${(!options?.frameOptions?.type || options?.frameOptions?.type === 'none') ? styles.disabled : ''}`}>
                        <div className={styles.colorGroup}>
                            <label className={styles.colorLabel}>Primary</label>
                            <button className={styles.swatch} onClick={() => setColorDisplay('primary')}>
                                <div className={styles.color} style={{background: options?.frameOptions?.primaryColor || '#000000'}} />
                            </button>
                            {colorDisplay === 'primary' && (
                                <div className={styles.popover}>
                                    <div className={styles.cover} onClick={() => setColorDisplay('')} />
                                    <ColorPicker 
                                        name='primary'
                                        color={options?.frameOptions?.primaryColor || '#000000'}
                                        onChange={handleColorChange}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.colorGroup}>
                            <label className={styles.colorLabel}>Secondary</label>
                            <button className={styles.swatch} onClick={() => setColorDisplay('secondary')}>
                                <div className={styles.color} style={{background: options?.frameOptions?.secondaryColor || '#FFFFFF'}} />
                            </button>
                            {colorDisplay === 'secondary' && (
                                <div className={styles.popover}>
                                    <div className={styles.cover} onClick={() => setColorDisplay('')} />
                                    <ColorPicker 
                                        name='secondary'
                                        color={options?.frameOptions?.secondaryColor || '#FFFFFF'}
                                        onChange={handleColorChange}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.colorGroup}>
                            <label className={styles.colorLabel}>Accent</label>
                            <button className={styles.swatch} onClick={() => setColorDisplay('accent')}>
                                <div className={styles.color} style={{background: options?.frameOptions?.accentColor || '#000000'}} />
                            </button>
                            {colorDisplay === 'accent' && (
                                <div className={styles.popover}>
                                    <div className={styles.cover} onClick={() => setColorDisplay('')} />
                                    <ColorPicker 
                                        name='accent'
                                        color={options?.frameOptions?.accentColor || '#000000'}
                                        onChange={handleColorChange}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.colorGroup}>
                            <label className={styles.colorLabel}>Color Reset</label>
                            <button className={styles.swatch} onClick={handleReset}><i className='far fa-arrow-u-turn-up-left' /></button>
                        </div>
                    </div>
                </div>
                <button className={styles.downloadBtn} onClick={handleDownload} disabled={loading}><i className='far fa-download' /> Download</button>
            </div>
            <div className={styles.right}>
                <div className={styles.qrWrap} style={{}}>
                    <QrRef size={size} childQrOptions={options}/>
                </div>
            </div>
            <div className={styles.blue} />
        </div>
    )
}